import React, { useEffect, useState, useMemo } from 'react';
import { 
  AddContainer, Component4, Texts, HeaderPart, FooterPart, 
  CancelButton, SaveButton 
} from './Style';
import InputSelect from 'src/components/shared/Form/Select';
import InputComponent from 'src/components/shared/Form/Input';
import { palette, shadows } from 'src/config/theme.js';
import { alpha } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../../../src/assets/images/close-circle.svg';
import { ReactComponent as AddIcon } from '../../../../../../src/assets/images/add-square.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getClinicProfileByIdRequest, getTemplateByClinicIdRequest, postMessageRequest, getClinicByIdRequest, getDoctorProfileByIdRequest } from 'src/modules/app/store/appActions';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import theme from 'src/config/theme.js';
import useAppointmentDetails from '../../UseAppointmentDetails';
import toast from 'src/utils/toast';
import useResponsive from 'src/components/hooks/useResponsive';
import { getUserFromToken } from 'src/DecodeToken';
import dayjs from 'dayjs';

const Language = [
  { label: 'English', value: 'english' },
  { label: 'Hindi', value: 'hindi' },
  { label: 'Gujarati', value: 'gujarati' },
];

const parseTemplateVariables = (text, patient, doctor, clinic) => {
  if (!text) return '';
  return text
    .replace(/\{\{clinic_name\}\}/gi, clinic?.clinicName || 'Clinic Name')
    .replace(/\{\{patient_name\}\}/gi, patient?.fullName || 'Patient Name')
    .replace(/\{\{doctor_name\}\}/gi, doctor?.fullName || 'Doctor Name')
    .replace(/\{\{appointment_date\}\}/gi, dayjs().format('D MMMM YYYY'))
    .replace(/\{\{appointment_time\}\}/gi, dayjs().add(10, 'minute').format('h:mm A'))
    .replace(/\{\{mobile_number\}\}/gi, patient?.contactNo || 'Mobile Number');
};

const FormObserver = ({ allTemplates, patient, doctor, clinic }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const Templates = allTemplates
      ?.filter((template) => (template.type || 'message').toLowerCase() === 'message')
      ?.filter((template) => {
        const lang = template.language?.toLowerCase();
        const selectedLang = values.language?.toLowerCase();

        if (lang) {
          return lang === selectedLang;
        }

        if (selectedLang === 'hindi') {
          return /[\u0900-\u097F]/.test(template.title) && !/[\u0A80-\u0AFF]/.test(template.title);
        } else if (selectedLang === 'gujarati') {
          return /[\u0A80-\u0AFF]/.test(template.title);
        } else {
          return !/[\u0900-\u097F\u0A80-\u0AFF]/.test(template.title);
        }
      }) || [];

    setFieldValue('title', '');
    setFieldValue('message', '');

    if (Templates.length > 0) {
      setFieldValue('title', Templates[0].id || Templates[0]._id || Templates[0].title);
      setFieldValue('message', parseTemplateVariables(Templates[0].text, patient, doctor, clinic));
    }

  }, [values.language, setFieldValue, allTemplates, patient, doctor, clinic]);

  return null;
};

function MessageTabForm({ onClose, fetchMessages, setOpenWhatsappDialog, setWhatsappMessage, setPhoneNumber }) {
  const { isMobile, isTablet } = useResponsive();
  const dispatch = useDispatch();
  const TemplateData = useSelector((state) => state.app.getTemplateByClinicId);
  const ClinicProfileData = useSelector((state) => state.app.getClinicProfileById);
  const { PatientId, clinicId } = useAppointmentDetails();
  const patientData = useSelector((state) => state.app.patientById);
  const contactNo = patientData?.payload?.data?.patient?.contactNo;
  const patientProps = patientData?.payload?.data?.patient;

  const [clinicProps, setClinicProps] = useState(null);
  const [doctorProps, setDoctorProps] = useState(null);

  useEffect(() => {
    if (clinicId) {
      dispatch(getTemplateByClinicIdRequest(clinicId));
      dispatch(getClinicProfileByIdRequest(clinicId));
      dispatch(getClinicByIdRequest(clinicId, (res) => {
          setClinicProps(res?.data || res);
      }));
    }
    const userId = getUserFromToken();
    if (userId) {
        dispatch(getDoctorProfileByIdRequest(userId, (res) => {
            setDoctorProps(res?.data || res);
        }));
    }
  }, [clinicId, dispatch]);

  const allTemplates = useMemo(() => {
    const getTemplatesFromSlice = (slice) => {
      if (Array.isArray(slice)) return slice;
      if (slice?.payload?.data && Array.isArray(slice.payload.data)) return slice.payload.data;
      if (slice?.data && Array.isArray(slice.data)) return slice.data;
      if (slice?.payload && Array.isArray(slice.payload)) return slice.payload;
      return [];
    };

    const templates1 = getTemplatesFromSlice(TemplateData);

    const profile = ClinicProfileData?.payload?.data || ClinicProfileData?.data || ClinicProfileData;
    let templates2 = [];
    if (profile?.messageTemplate && Array.isArray(profile.messageTemplate)) {
      templates2 = profile.messageTemplate;
    } else if (profile?.profile?.messageTemplate && Array.isArray(profile.profile.messageTemplate)) {
      templates2 = profile.profile.messageTemplate;
    } else if (Array.isArray(profile?.[0]?.messageTemplate)) {
      templates2 = profile[0].messageTemplate;
    }

    // Combine and ensure each has a unique identifier
    return [...templates1, ...templates2].map((t, idx) => ({
      ...t,
      id: t.id || t._id || `temp-${idx}`
    }));
  }, [TemplateData, ClinicProfileData]);

  const validationSchema = Yup.object().shape({
    message: Yup.string().required('Message is required'),
    title: Yup.string().required('Template must be selected'),
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const messageData = {
        language: values.language,
        title: values.title,
        message: values.message,
        clinic: clinicId,
        patient: PatientId,
      };
      const response = await new Promise((resolve, reject) => {
        dispatch(postMessageRequest(messageData, resolve, reject));
      });

      toast.success('Message added successfully');
      if (response?.message?.id && fetchMessages) {
        fetchMessages();
      }

      setWhatsappMessage(values.message);
      setPhoneNumber(`91${contactNo}` || "");
      setOpenWhatsappDialog(true); 
      onClose();
      resetForm();
    } catch (error) {
      console.log(error.message || 'Failed to add message');
    }
  };

  return (
    <Formik
      initialValues={{
        message: '',
        language: Language[0].value,
        title: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleBlur, handleChange, setFieldValue }) => {
        const Templates = allTemplates
          ?.filter((template) => (template.type || 'message').toLowerCase() === 'message')
          ?.filter((template) => {
            const lang = template.language?.toLowerCase();
            const selectedLang = values.language?.toLowerCase();

            if (lang) {
              return lang === selectedLang;
            }

            if (selectedLang === 'hindi') {
              return /[\u0900-\u097F]/.test(template.title) && !/[\u0A80-\u0AFF]/.test(template.title);
            } else if (selectedLang === 'gujarati') {
              return /[\u0A80-\u0AFF]/.test(template.title);
            } else {
              return !/[\u0900-\u097F\u0A80-\u0AFF]/.test(template.title);
            }
          })
          ?.map((template) => ({
            label: template.title,
            value: template.id,
          })) || [];

        return (
          <Form>
            <FormObserver allTemplates={allTemplates} patient={patientProps} doctor={doctorProps} clinic={clinicProps} />

            <HeaderPart>
              Add Message
              <CloseIcon
                width={isMobile || isTablet ? 18 : 24}
                height={isMobile || isTablet ? 18 : 24}
                onClick={onClose}
                style={{ cursor: 'pointer' }}
              />
            </HeaderPart>

            <AddContainer>
              <Component4>
                <Texts>Language</Texts>
                <InputSelect
                  name="language"
                  value={values.language}
                  options={Language}
                  onChange={(value) => setFieldValue('language', value)}
                  variant="outlined"
                  placeholder="Select"
                  showNone={false}
                  sx={{
                    boxShadow: theme.shadows[1],
                    '& .MuiOutlinedInput-root': {
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '12px',
                    },
                  }}
                />
              </Component4>

              <Component4>
                <Texts>Message Template</Texts>
                <InputSelect
                  name="title"
                  value={values.title}
                  options={Templates}
                  onChange={(value) => {
                    setFieldValue('title', value);
                    const selectedTemplate = allTemplates.find(
                      (template) => template.id === value
                    );
                    setFieldValue('message', selectedTemplate ? parseTemplateVariables(selectedTemplate.text, patientProps, doctorProps, clinicProps) : '');
                  }}
                  showNone={false}
                  variant="outlined"
                  placeholder="Select"
                  onBlur={handleBlur}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                  sx={{
                    boxShadow: theme.shadows[1],
                    '& .MuiOutlinedInput-root': {
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '12px',
                    },
                  }}
                />
              </Component4>

              <Component4>
                <Texts>Message</Texts>
                <InputComponent
                  name="message"
                  value={values.message}
                  variant="outlined"
                  placeholder="Enter"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.message && Boolean(errors.message)}
                  helperText={touched.message && errors.message}
                  fullWidth
                  multiline
                  rows={4}
                  sx={{
                    backgroundColor: palette.gery.white,
                    boxShadow: shadows[1],
                    '& .MuiOutlinedInput-root': {
                      padding: '16px 18px',
                      borderRadius: '12px',
                      height: '120px',
                    },
                  }}
                />
              </Component4>
            </AddContainer>

            <FooterPart>
              <CancelButton
                variant="none"
                startIcon={<CloseIcon width={isMobile || isTablet ? 18 : 24} height={isMobile || isTablet ? 18 : 24} />}
                onClick={onClose}
              >
                Cancel
              </CancelButton>
              <SaveButton
                type="submit"
                variant="outlined"
                startIcon={<AddIcon width={isMobile || isTablet ? 18 : 24} height={isMobile || isTablet ? 18 : 24} />}
              >
                Save
              </SaveButton>
            </FooterPart>
          </Form>
        );
      }}
    </Formik>
  );
}

export default MessageTabForm;
